/**
 * The wire protocol.
 *
 * ONE RULE, and it is the reason this project can hold money:
 *
 *   CLIENTS SEND INPUT. SERVERS SEND TRUTH.
 *
 * There is no message in this file that lets a client assert a position, a
 * death, a pickup, a hit, or a win. v1 had six of those (`move`, `died`,
 * `collect`, `trigger_activated`, `respawn`, `platform_step`) and each one was
 * a way to take money out of other players' pockets with ten lines of
 * JavaScript.
 *
 * If you are adding a client->server message and it contains a noun rather than
 * a button, stop.
 */
// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------
/** Server simulation rate. Fixed timestep, integer ticks, no wall clock inside a step. */
export const TICK_HZ = 30;
export const TICK_MS = 1000 / TICK_HZ;
/** How often the server ships a snapshot. */
export const SNAPSHOT_HZ = 15;
/**
 * How far behind the newest snapshot the client renders remote entities.
 * Buys one dropped packet plus jitter without visible rubber-banding.
 */
export const INTERPOLATION_DELAY_MS = 100;
/** Inputs the server will accept per second per connection before it starts dropping. */
export const MAX_INPUTS_PER_SEC = TICK_HZ * 2;
/** How many ticks of history the server keeps for reconciliation and audit. */
export const SNAPSHOT_HISTORY_TICKS = TICK_HZ * 2;
// ---------------------------------------------------------------------------
// Client -> Server
// ---------------------------------------------------------------------------
/**
 * Bitfield. One bit per button. Never a position.
 *
 * NOT a `const enum`. A const enum lands in the emitted .d.ts as
 * `declare const enum Button`, and every workspace package compiles with
 * `verbatimModuleSyntax`, under which reading an AMBIENT const enum is TS2748.
 * That breaks the moment a consumer resolves this package through its built
 * dist/ instead of its source — i.e. every `turbo build`, while the
 * source-path typecheck stays green. A plain enum emits the same runtime object
 * and the same numbers, and `apps/game` reads `Button` as a value at runtime.
 */
export var Button;
(function (Button) {
    Button[Button["Jump"] = 1] = "Jump";
    Button[Button["Dash"] = 2] = "Dash";
    Button[Button["Basic"] = 4] = "Basic";
    Button[Button["Skill1"] = 8] = "Skill1";
    Button[Button["Skill2"] = 16] = "Skill2";
    Button[Button["Ultimate"] = 32] = "Ultimate";
})(Button || (Button = {}));
// ---------------------------------------------------------------------------
// Validation — the server's first line, before the sim sees anything
// ---------------------------------------------------------------------------
/** Largest yaw change accepted in a single tick, radians. Rejects instant-snap aim scripts. */
export const MAX_YAW_DELTA_PER_TICK = Math.PI;
export function sanitizeInputFrame(raw) {
    if (typeof raw !== "object" || raw === null)
        return null;
    const r = raw;
    const tick = r["tick"];
    const seq = r["seq"];
    const buttons = r["buttons"];
    const moveX = r["moveX"];
    const moveZ = r["moveZ"];
    const yaw = r["yaw"];
    if (!Number.isFinite(tick) || !Number.isFinite(seq))
        return null;
    if (!Number.isFinite(buttons))
        return null;
    if (!Number.isFinite(moveX) || !Number.isFinite(moveZ) || !Number.isFinite(yaw))
        return null;
    // Clamp rather than reject: a legitimate client with a rounding artifact
    // should keep playing, while a client claiming moveX=40 gets moveX=1.
    const mx = clamp(moveX, -1, 1);
    const mz = clamp(moveZ, -1, 1);
    const mag = Math.hypot(mx, mz);
    const nx = mag > 1 ? mx / mag : mx;
    const nz = mag > 1 ? mz / mag : mz;
    return {
        tick: Math.trunc(tick),
        seq: Math.trunc(seq),
        buttons: buttons & 0b111111,
        moveX: nx,
        moveZ: nz,
        yaw: normalizeAngle(yaw),
    };
}
function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
export function normalizeAngle(a) {
    const twoPi = Math.PI * 2;
    let x = a % twoPi;
    if (x > Math.PI)
        x -= twoPi;
    if (x < -Math.PI)
        x += twoPi;
    return x;
}
//# sourceMappingURL=wire.js.map